// Pre-redesign flat sidebar, kept only while NEW_GUI_ENABLED / useNewGui can
// select the old GUI. Delete this file (and the branch in SideBar.tsx) once
// the redesign is the only GUI. See .cursor/rules/naming-parallel-implementations.mdc.
import React from 'react';
import { Link } from 'react-router-dom';
import { FiMenu, FiX, FiUsers, FiUserCheck, FiUserPlus, FiKey, FiHome, FiServer, FiCalendar, FiSearch, FiBarChart2, FiLock, FiSettings, FiTrash2, FiBox, FiHardDrive, FiLayers, FiMonitor, FiShield, FiHelpCircle } from 'react-icons/fi';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useTranslation } from '../../locales/useTranslation';
import { useSystemSettings } from '../../hooks/useSystemSettings';

const SideBarLegacy: React.FC = () => {
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const user = useSelector((state: RootState) => state.auth.user);
    const t = useTranslation();
    const { settings } = useSystemSettings();

    return (
        <div>
            {/* Sidebar */}
        <div
            className={`fixed top-[120px] left-0 h-[calc(100vh-120px)] bg-blue-900 text-white transition-all duration-300 z-40 ${
                isSidebarOpen ? 'w-64' : 'w-16'
            }`}
        >
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-4">
                    <h1 className={`${isSidebarOpen ? 'block' : 'hidden'} text-2xl font-bold`}>Dashboard</h1>
                    <button onClick={toggleSidebar} className="focus:outline-none">
                        {isSidebarOpen ? (
                            <FiX className="text-3xl" /> // Icon when sidebar is open
                        ) : (
                            <FiMenu className="text-3xl" /> // Icon when sidebar is closed
                        )}
                    </button>
                </div>

                {/* Sidebar Navigation */}
                <nav className="mt-10">
                    <ul>
                        <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                            <Link to="/" className="flex items-center">
                                <FiHome className="text-xl" />
                                <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>{t.systemDashboard}</span>
                            </Link>
                        </li>
                        <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                            <Link to="/help" className="flex items-center">
                                <FiHelpCircle className="text-xl" />
                                <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Help</span>
                            </Link>
                        </li>


                        {/* Manage Users - For roles that can view/manage users */}
                        {(user?.role === 'itoperator' || user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/uniqueusers" className="flex items-center">
                                    <FiUserCheck className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>{t.manageUsers}</span>
                                </Link>
                            </li>
                        )}
                        {/* Setup Users - Only for itoperator and itadmin */}
                        {(user?.role === 'itoperator' || user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/gen-users" className="flex items-center">
                                    <FiUserPlus className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>{t.setupUsers}</span>
                                </Link>
                            </li>
                        )}
                        {/* License Administration - For roles that can manage licenses */}
                        {(user?.role === 'itoperator' || user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/licmgmt" className="flex items-center">
                                    <FiKey className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>{t.licenseAdministration}</span>
                                </Link>
                            </li>
                        )}

                        {/* Extension Approvals - For itadmin and approver roles */}
                        {(user?.role === 'itadmin' || user?.role === 'approver' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/extension-approvals" className="flex items-center">
                                    <FiCalendar className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Extension Approvals</span>
                                </Link>
                            </li>
                        )}

                        {/* Infra Users Admin - For roles that can manage servers */}
                        {(user?.role === 'itoperator' || user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/user-table" className="flex items-center">
                                    <FiUsers className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>{t.serverAccounts}</span>
                                </Link>
                            </li>
                        )}

                        {/* Server Info - Base VNC port per server */}
                        {(user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/server-info" className="flex items-center">
                                    <FiServer className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Server Info</span>
                                </Link>
                            </li>
                        )}

                        {/* Password Reset - itadmin and superadmin, only when feature is enabled */}
                        {(user?.role === 'itadmin' || user?.role === 'superadmin') && settings.PASSWORD_RESET_ENABLED === 'true' && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/pwdreset" className="flex items-center">
                                    <FiLock className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Password Reset</span>
                                </Link>
                            </li>
                        )}

                        {/* Cleanup Tracking - itadmin and superadmin */}
                        {(user?.role === 'itadmin' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/cleanup-tracking" className="flex items-center">
                                    <FiTrash2 className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Cleanup Tracking</span>
                                </Link>
                            </li>
                        )}

                        {/* Workspace — gated by WORKSPACE_CONTAINER_ENABLED */}
                        {settings.WORKSPACE_CONTAINER_ENABLED === 'true' && (user?.role === 'itadmin' || user?.role === 'superadmin' || user?.role === 'itoperator') && (
                            <>
                                <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                    <Link to="/workspace/definitions" className="flex items-center">
                                        <FiBox className="text-xl" />
                                        <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>WS Definitions</span>
                                    </Link>
                                </li>
                                <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                    <Link to="/workspace/nfs" className="flex items-center">
                                        <FiHardDrive className="text-xl" />
                                        <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>WS NFS Locations</span>
                                    </Link>
                                </li>
                                <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                    <Link to="/workspace/groups" className="flex items-center">
                                        <FiLayers className="text-xl" />
                                        <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>WS Groups</span>
                                    </Link>
                                </li>
                                <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                    <Link to="/workspace/sessions" className="flex items-center">
                                        <FiMonitor className="text-xl" />
                                        <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>WS Sessions</span>
                                    </Link>
                                </li>
                            </>
                        )}

                        {/* User Administration - superadmin only */}
                        {user?.role === 'superadmin' && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/user-management" className="flex items-center">
                                    <FiShield className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>User Administration</span>
                                </Link>
                            </li>
                        )}

                        {/* Configuration - superadmin only */}
                        {user?.role === 'superadmin' && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/system-settings" className="flex items-center">
                                    <FiSettings className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Configuration</span>
                                </Link>
                            </li>
                        )}

                        {/* Audit Logs - Only for auditors and superadmin */}
                        {(user?.role === 'auditor' || user?.role === 'superadmin') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/audit" className="flex items-center">
                                    <FiSearch className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Audit Logs</span>
                                </Link>
                            </li>
                        )}

                        {/* Login Activity Report - For roles that can view summary */}
                        {(user?.role === 'itoperator' || user?.role === 'itadmin' || user?.role === 'observer' || user?.role === 'superadmin' || user?.role === 'approver') && (
                            <li className="px-4 py-2 hover:bg-blue-800 flex items-center">
                                <Link to="/reports/login-activity" className="flex items-center">
                                    <FiBarChart2 className="text-xl" />
                                    <span className={`${isSidebarOpen ? 'ml-4' : 'hidden'} text-xl`}>Login Activity Report</span>
                                </Link>
                            </li>
                        )}

                    </ul>
                </nav>
            </div>
        </div>
    );
};

export default SideBarLegacy;
